import React from 'react';
import { Coins, X, AlertCircle } from 'lucide-react';
import type { Pet } from '../../types/pets';
import type { Item } from '../../types/items';

interface PurchaseModalProps {
  item: Pet | Item;
  userPoints: number;
  onConfirm: () => void;
  onCancel: () => void;
  isProcessing?: boolean;
  error?: string | null;
}

export function PurchaseModal({ 
  item, 
  userPoints, 
  onConfirm, 
  onCancel,
  isProcessing,
  error
}: PurchaseModalProps) {
  const price = 'basePrice' in item ? item.basePrice : item.price;
  const canAfford = userPoints >= price;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-gradient-to-br from-yellow-600 to-amber-500 p-0.5 rounded-xl max-w-md w-full">
        <div className="bg-gray-900 p-6 rounded-lg">
          <div className="flex justify-between items-start mb-6">
            <h3 className="text-xl font-bold text-white">Confirm Purchase</h3>
            <button 
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-300"
              disabled={isProcessing}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img
                src={item.imageUrl}
                alt={item.name}
                className="w-16 h-16 rounded-lg object-cover"
              />
              <div>
                <h4 className="font-bold text-white">{item.name}</h4>
                <div className="text-sm text-gray-400 capitalize">
                  {item.rarity} {('type' in item) ? item.type : ''}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center p-3 bg-gray-800 rounded-lg">
              <div className="text-gray-400">Price:</div>
              <div className="flex items-center gap-1 text-yellow-400 font-mono">
                <Coins className="w-4 h-4" />
                {price.toLocaleString()}
              </div>
            </div>

            <div className="flex justify-between items-center p-3 bg-gray-800 rounded-lg">
              <div className="text-gray-400">Your Balance:</div>
              <div className="flex items-center gap-1 text-yellow-400 font-mono">
                <Coins className="w-4 h-4" />
                {userPoints.toLocaleString()}
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 bg-red-400/10 p-3 rounded-lg">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={onCancel}
                disabled={isProcessing}
                className="flex-1 px-4 py-2 bg-gray-800 text-gray-300 rounded-lg
                  hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={!canAfford || isProcessing}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-yellow-600 to-amber-500
                  text-white rounded-lg hover:from-yellow-500 hover:to-amber-400
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Confirm Purchase'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}